import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Typography, Box, Card, CardContent, CircularProgress, Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Chart from 'react-apexcharts';
import { CheckCircle, People, LocationOn } from '@mui/icons-material';
import { primaryColor } from '../theme';
import { Link } from 'react-router-dom';
import moment from 'moment';

const HeadOfficeAdminDashboard = () => {
  const theme = useTheme();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin-dashboard`);
        setDashboardData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">Dashboard data not found</Typography>
      </Container>
    );
  }

  const { contentData, userData, regionalData, recentActivity, viewsDownloadsData, regionalActivityData, topRegionsData } = dashboardData;

  const chartOptions = {
    chart: {
      type: 'area',
      height: 350,
      zoom: {
        enabled: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth'
    },
    title: {
      text: 'Content Views and Downloads Trend',
      align: 'left',
      style: {
        color: primaryColor, // Use the primary color for the title
        fontSize: '24px',
        fontWeight: 'bold'
      }
    },
    xaxis: {
      categories: viewsDownloadsData.map(item => item.date)
    },
    tooltip: {
      x: {
        format: 'dd/MM/yy HH:mm'
      },
    },
    colors: [theme.palette.primary.main, theme.palette.secondary.main]
  };

  const chartSeries = [
    {
      name: 'Views',
      data: viewsDownloadsData.map(item => item.views)
    },
    {
      name: 'Downloads',
      data: viewsDownloadsData.map(item => item.downloads)
    }
  ];

  const regionalActivityChartOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      height: 350,
    },
    plotOptions: {
      bar: {
        horizontal: false,
      },
    },
    title: {
      text: 'Regional Activity',
      align: 'left',
      style: {
        color: primaryColor, // Use the primary color for the title
        fontSize: '24px',
        fontWeight: 'bold'
      }
    },
    xaxis: {
      categories: regionalActivityData.categories,
    },
    colors: [theme.palette.primary.main, theme.palette.success.main, theme.palette.error.main],
  };

  const topRegionsChartOptions = {
    chart: {
      type: 'bar',
      height: 350
    },
    plotOptions: {
      bar: {
        horizontal: true,
      },
    },
    title: {
      text: 'Content Views per Region',
      align: 'left',
      style: {
        color: primaryColor, // Use the primary color for the title
        fontSize: '24px',
        fontWeight: 'bold'
      }
    },
    xaxis: {
      categories: topRegionsData.categories,
    },
    colors: [theme.palette.primary.main],
  };

  const getTimeAgo = (date) => {
    return moment(date).fromNow();
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'content':
        return <CheckCircle />;
      case 'user':
        return <People />;
      default:
        return <People />;
    }
  };

  const user = JSON.parse(localStorage.getItem('user')) || { firstName: 'User', role: 'Role' };

  return (
    <Container maxWidth="false" sx={{ mt: 4, display: 'flex' }}>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ width: '100%', mb: 2 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: primaryColor,
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            Hello, <span style={{ fontSize: '24px' }}>Welcome to <span style={{ fontWeight: 'bold' }}>{user.role}</span> Dashboard!</span>
          </Typography>
        </Box>
        <Box display="flex" flexWrap="wrap" mb={4}>
          <Box sx={{ flex: '1 1 33%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Total Content</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{contentData.total}</Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={1}>
                      <Typography variant="body2" sx={{ color: theme.palette.success.main }}>Approved: {contentData.approved}</Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.warning.main }}>Pending: {contentData.pending}</Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.error.main }}>Rejected: {contentData.rejected}</Typography>
                    </Box>
                  </Box>
                  <CheckCircle fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 33%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>User Statistics</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{userData.total}</Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={1}>
                      <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>Admins: {userData.roles.admin}</Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.secondary.main }}>Coordinators: {userData.roles.coordinator}</Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.info.main }}>Editors: {userData.roles.editor}</Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.warning.main }}>Reviewers: {userData.roles.reviewer}</Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.info.main }}>Trainers: {userData.roles.branchManagers}</Typography>
                    </Box>
                  </Box>
                  <People fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 33%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Users Per Region Summary</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{regionalData.totalRegions}</Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={1}>
                      {regionalData.regions.map(region => (
                      <Typography key={region.location} variant="body2" sx={{ color: theme.palette.primary.main }}>
                        {region.location}: {region.userCount}
                      </Typography>
                      ))}
                    </Box>
                  </Box>
                  <LocationOn fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
        <Box sx={{ mb: 4 }}>
          <Chart options={chartOptions} series={chartSeries} type="area" height={350} />
        </Box>
        {/* <Box sx={{ mb: 4 }}>
          <Chart options={regionalActivityChartOptions} series={regionalActivityData.series} type="bar" height={350} />
        </Box> */}
        <Box sx={{ mb: 4 }}>
          <Chart options={topRegionsChartOptions} series={topRegionsData.series} type="bar" height={350} />
        </Box>
      </Box>

      <Box sx={{ display: { xs: 'none', md: 'block' }, width: '25%', ml: 4 }}>
        <Typography variant="h5" gutterBottom style={{ color: primaryColor }}>
          Recent Activity
        </Typography>
        <Box sx={{ position: 'relative' }}>
          {recentActivity.map((activity, index) => (
            <Box key={activity.id} display="flex" alignItems="center" mb={2} position="relative">
              <Avatar sx={{ bgcolor: primaryColor, mr: 2 }}>
                {getActivityIcon(activity.type)}
              </Avatar>
              <Box>
                <Box display="flex" alignItems="center" mb={1} gap={1}>
                  <Typography variant="body1">{activity.User.firstName} ({activity.User.role})</Typography>
                  <Typography variant="caption" color="textSecondary">{getTimeAgo(activity.date)}</Typography>
                </Box>
                <Typography variant="body2">{activity.activity}</Typography>
              </Box>
              {index < recentActivity.length - 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: '20px',
                    top: 'calc(60% + 20px)', // Adjust the top position to start below the icon
                    bottom: '-20px',
                    width: '2px',
                    bgcolor: primaryColor,
                  }}
                />
              )}
            </Box>
          ))}
          <Link to="/logs" style={{ textDecoration: 'none', color: primaryColor }}>
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              View More
            </Typography>
          </Link>
        </Box>
      </Box>
    </Container>
  );
};

export default HeadOfficeAdminDashboard;